import { faUsers } from '@fortawesome/pro-regular-svg-icons';
import * as React from 'react';

import { withIcon } from './Icon.tsx';
import classnames from '../utils/classnames.tsx';

const TeamIcon = withIcon(faUsers);

export interface AvatarPerson {
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
}

const SIZES = {
    sm: 'h-6 w-6 text-xs',
    md: 'h-8 w-8 text-sm',
} as const;

export const initialsOf = (person: AvatarPerson) => {
    const parts = [person.firstName, person.lastName].filter(Boolean) as string[];
    if (parts.length > 0)
        return parts
            .map((part) => part[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
    return (person.email || '').slice(0, 2).toUpperCase();
};

export const personLabel = (person: AvatarPerson) =>
    [person.firstName, person.lastName].filter(Boolean).join(' ') || person.email || '';

export interface AvatarProps {
    person?: AvatarPerson;
    size?: keyof typeof SIZES;
    className?: string;
    children?: React.ReactNode;
    title?: string;
}

/** Initials in a circle for a person; `children` replaces the initials (e.g. a team icon). */
const Avatar = ({ person, size = 'sm', className, children, title }: AvatarProps) => (
    <span
        title={title ?? (person ? personLabel(person) : undefined)}
        className={classnames(
            'inline-flex flex-none select-none items-center justify-center rounded-full border border-solid border-line-weak bg-surface-medium font-medium text-content-strong',
            SIZES[size],
            className
        )}
    >
        {children ?? (person ? initialsOf(person) : null)}
    </span>
);

export type TeamAvatarProps = Omit<AvatarProps, 'person' | 'children'>;

export const TeamAvatar = ({ size = 'sm', className, title }: TeamAvatarProps) => (
    <Avatar size={size} className={classnames('text-content-medium', className)} title={title}>
        <TeamIcon />
    </Avatar>
);

export default Avatar;
